import type { UdsTemplatePage } from '../../types/admin.types';

/**
 * Minimal TemplatePage shape used by the admin landing-page tab.
 * Mirrors the UDS schema with camelCase field names — `recordToTemplatePage`
 * adapts a raw UDS row into this shape so the form can keep its current
 * field names.
 */
export interface TemplatePage {
  id: string;
  slug: string;
  locale: string;
  title: string;
  description: string;
  htmlBody: string;
  ogTitle?: string | null;
  ogDescription?: string | null;
  ogImage?: string | null;
  keywords?: string[];
  published: boolean;
  blocks?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ---------------------------------------------------------------------------
// UDS shapes — PascalCase, exactly as the gateway returns / accepts.
// ---------------------------------------------------------------------------

export interface TemplatePageInsertInput {
  Slug: string;
  Locale: string;
  Title: string;
  Description: string;
  HtmlBody: string;
  OgTitle: string | null;
  OgDescription: string | null;
  OgImage: string | null;
  Keywords: string[];
  Published: boolean;
  Blocks: string;
}

export type TemplatePageUpdateInput = Partial<TemplatePageInsertInput>;

export type TemplatePageRecord = UdsTemplatePage;

export type { GetTemplatePagesResponse } from '../../types/admin.types';

// ---------------------------------------------------------------------------
// AI-generation shape — matches what the old generate endpoint returned.
// `blocks` stays opaque to the client; the form serializes it into the
// HtmlBody field for storage compatibility.
// ---------------------------------------------------------------------------

export interface LandingGenerationResult {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string | null;
  keywords: string[];
  blocks: unknown[];
}

/** Adapter — convert a raw UDS row into the UI-facing TemplatePage shape. */
export function recordToTemplatePage(r: TemplatePageRecord): TemplatePage {
  return {
    id: r.ItemId,
    slug: r.Slug,
    locale: r.Locale,
    title: r.Title,
    description: r.Description ?? '',
    htmlBody: r.HtmlBody ?? '',
    ogTitle: r.OgTitle ?? null,
    ogDescription: r.OgDescription ?? null,
    ogImage: r.OgImage ?? null,
    keywords: r.Keywords ?? [],
    published: r.Published ?? false,
    blocks: r.Blocks ?? '',
    createdAt: r.CreatedDate,
    updatedAt: r.LastUpdatedDate,
  };
}
